import { z } from 'zod';
import type {
  Conversation,
  ConversationMessage,
  Image,
  NationalityPerson,
  PartyRole,
  VehicleOwnerKind,
  VehicleTypes,
} from '@hero/schema';
import type { OrderWaybillBayanStatus } from './app.api';
import {
  conversationChannelTypeSchema,
  conversationStatusSchema,
  dateTypeSchema,
  imageWithUploadSchema,
  itemPackingSchema,
  languageSchema,
  objectIdSchema,
  searchColSchema,
  tripLoadTypeSchema,
  vehicleOwnerKindSchema,
  vehiclePlateTypeSchema,
} from '@hero/schema';
import { API } from '..';

/**
 * Hero AI agent API contracts.
 *
 * Note on streaming: `POST /agent/messages` returns a Server-Sent
 * Events stream — `data` is the SSE event union and is consumed via
 * an async iterator on the SDK side, not as a single payload. Other
 * routes (`/agent/messages/confirm`, `/agent/conversations`, …) use
 * standard JSON request/response.
 *
 * See [`docs/ai/architecture.md`](../../../../docs/ai/architecture.md)
 * "SSE event catalog" for the full event shape.
 */

/**
 * Specialist intents the supervisor router knows about. Mirrors the
 * backend `Intent` union in `supervisor.graph.ts`. When the client
 * already knows the domain (e.g. clicking the "Orders summary" preset
 * chip), it can pass `presetIntent` to bypass the classifier roundtrip
 * (saves ~300–800 ms of Haiku latency on the hot path).
 *
 * Keep this in sync with backend `ALL_INTENTS`.
 */
export const agentPresetIntentSchema = z.enum([
  'order',
  'places',
  'contact',
  'client',
  'fleet',
  'driver',
  'invoice',
  'transaction',
  'financing',
  'billing',
  'org',
  'sales',
  'pricing',
  'offer',
  'account',
  'escalate',
  'chat',
]);
export type AgentPresetIntent = z.infer<typeof agentPresetIntentSchema>;

//* send a chat message (SSE-streamed)
export const agentSendMessageAPISchema = z.object({
  text: z.string().min(1).max(4_000),
  conversationId: z.string().optional(),
  /**
   * Optional client-known intent. When present AND valid for the
   * caller's persona, the backend skips the supervisor classifier
   * (Haiku roundtrip) and routes directly to the matching specialist
   * tool partition. Used by the empty-state quick-start chips and any
   * other UI surface that already knows what domain the user is in.
   * Empty/missing → backend falls back to the classifier as before.
   */
  presetIntent: agentPresetIntentSchema.optional(),
});
/**
 * The response is a Server-Sent Events stream. Consumers iterate
 * `AgentSSEEvent` chunks via the SDK helper rather than awaiting a
 * single body. The `data` slot here is a marker / signal-only type
 * for the API hook — actual stream values come from
 * `agentSendMessageStream` on the SDK.
 */
export type AgentSendMessageAPI = API<
  z.infer<typeof agentSendMessageAPISchema>,
  { runId: string; conversationId: string }
>;

//* WhatsApp inbound (Buraq → Hero webhook)
const agentWhatsAppInboundLocationSchema = z
  .object({
    lat: z.number(),
    lng: z.number(),
    name: z.string().optional(),
    address: z.string().optional(),
  })
  .describe('Native WhatsApp location pin from the sender.');

const agentWhatsAppInboundMediaSchema = z
  .object({
    mediaId: z.string().min(1),
    mediaKind: z.enum(['image', 'document']),
    mimeType: z.string().optional(),
    caption: z.string().optional(),
    filename: z.string().optional(),
  })
  .describe('Inbound image or document reference from Buraq.');

export const agentWhatsAppInboundMessageSchema = z
  .object({
    id: z
      .union([z.string().min(1), z.number()])
      .optional()
      .describe('Provider message id (wamid) — optional; Hero generates one when omitted.'),
    messageId: z.union([z.string().min(1), z.number()]).optional(),
    message_id: z.union([z.string().min(1), z.number()]).optional(),
    wamid: z.union([z.string().min(1), z.number()]).optional(),
    from: z
      .union([z.string().min(1), z.number()])
      .optional()
      .describe('Sender phone in E.164 without the leading +.'),
    phone: z
      .union([z.string().min(1), z.number()])
      .optional()
      .describe('Alias for `from` — Buraq session phone variable.'),
    wa_id: z.union([z.string().min(1), z.number()]).optional(),
    senderPhone: z.union([z.string().min(1), z.number()]).optional(),
    sender_phone: z.union([z.string().min(1), z.number()]).optional(),
    message: z.string().min(1).optional().describe('Plain-text body from the sender.'),
    text: z.string().min(1).optional().describe('Alias for `message`.'),
    body: z.string().min(1).optional().describe('Alias for `message`.'),
    messageText: z.string().min(1).optional(),
    message_text: z.string().min(1).optional(),
    profileName: z.string().optional().describe('Sender display name from WhatsApp.'),
    profile_name: z.string().optional(),
    name: z.string().optional(),
    displayName: z.string().optional(),
    phoneNumberId: z
      .string()
      .optional()
      .describe('Receiving business line id — resolves to a ChannelAccount.'),
    phone_number_id: z.string().optional(),
    lineId: z.string().optional(),
    line_id: z.string().optional(),
    location: agentWhatsAppInboundLocationSchema.optional(),
    media: agentWhatsAppInboundMediaSchema.optional(),
  })
  .strip()
  .superRefine((row, ctx) => {
    if (
      row.from == null &&
      row.phone == null &&
      row.wa_id == null &&
      row.senderPhone == null &&
      row.sender_phone == null
    ) {
      ctx.addIssue({
        code: 'custom',
        message: 'from or phone is required',
        path: ['from'],
      });
    }
    const messageBody = row.message ?? row.text ?? row.body ?? row.messageText ?? row.message_text;
    if (!messageBody?.trim()) {
      ctx.addIssue({
        code: 'custom',
        message: 'message (or text / body) is required',
        path: ['message'],
      });
    }
  })
  .transform(row => {
    const from = normalizeInboundPhone(
      row.from ?? row.phone ?? row.wa_id ?? row.senderPhone ?? row.sender_phone!,
    );
    const message = (row.message ?? row.text ?? row.body ?? row.messageText ?? row.message_text)!;
    const rawId = row.id ?? row.messageId ?? row.message_id ?? row.wamid;
    const trimmedId = rawId != null ? String(rawId).trim() : '';
    return {
      id: trimmedId || `buraq-synth:${from}:${Date.now()}`,
      from,
      message,
      profileName: row.profileName ?? row.profile_name ?? row.name ?? row.displayName,
      phoneNumberId: row.phoneNumberId ?? row.phone_number_id ?? row.lineId ?? row.line_id,
      location: row.location,
      media: row.media,
    };
  })
  .describe('One normalized inbound WhatsApp message from Buraq.');

const agentWhatsAppInboundStatusSchema = z.object({
  externalMessageId: z.string().min(1),
  status: z.enum(['pending', 'sent', 'delivered', 'read', 'failed']),
});

/** Batch envelope — `{ messages: [...], statuses?: [...] }`. */
const agentWhatsAppInboundBatchSchema = z
  .object({
    messages: z.array(agentWhatsAppInboundMessageSchema).min(1),
    statuses: z.array(agentWhatsAppInboundStatusSchema).optional(),
  })
  .strict();

/** Delivery receipts only — `{ statuses: [...] }`. */
const agentWhatsAppInboundStatusBatchSchema = z
  .object({
    statuses: z.array(agentWhatsAppInboundStatusSchema).min(1),
  })
  .strict();

/** Buraq may POST `{}` on trigger/test before variables are mapped. */
const agentWhatsAppInboundEmptySchema = z.object({}).strict();

const agentWhatsAppInboundUnionSchema = z.union([
  agentWhatsAppInboundMessageSchema,
  agentWhatsAppInboundBatchSchema,
  agentWhatsAppInboundStatusBatchSchema,
  z.array(agentWhatsAppInboundMessageSchema).min(1),
  agentWhatsAppInboundEmptySchema,
]);

/**
 * POST /agent/whatsapp/buraq/inbound — Buraq HTTP-request webhook.
 * Accepts a single message object, a batch envelope, status receipts,
 * an array of messages, or an empty object (no-op).
 */
export const agentWhatsAppInboundAPISchema = z.preprocess(input => {
  if (input === undefined || input === null) return {};
  if (typeof input === 'string') {
    const trimmed = input.trim();
    if (!trimmed) return {};
    try {
      return JSON.parse(trimmed) as unknown;
    } catch {
      return input;
    }
  }
  return input;
}, agentWhatsAppInboundUnionSchema);

const normalizeInboundPhone = (value: string | number): string => String(value).replace(/\D/g, '');

export const agentWhatsAppInboundResponseSchema = z.object({
  message: z
    .string()
    .describe(
      'Human-readable acknowledgment that the webhook payload was accepted for background processing.',
    ),
});

export type AgentWhatsAppInboundAPI = API<
  z.infer<typeof agentWhatsAppInboundAPISchema>,
  z.infer<typeof agentWhatsAppInboundResponseSchema>
>;

/**
 * POST /agent/whatsapp/360dialog/inbound — 360dialog Cloud API webhook.
 * Meta-shaped envelope (`entry[].changes[].value`). Parsed in the
 * backend service; this schema only keeps the body intact.
 */
export const agentWhatsAppCloudInboundAPISchema = z
  .object({
    object: z
      .string()
      .optional()
      .describe('Meta webhook object type. Typically `whatsapp_business_account`.'),
    entry: z
      .array(z.unknown())
      .optional()
      .describe(
        'Meta webhook entries. Each change.value carries metadata.phone_number_id, messages[], and statuses[].',
      ),
  })
  .passthrough()
  .describe(
    '360dialog Cloud API inbound webhook (Meta envelope). Do not reuse the Buraq inbound schema.',
  );

export const agentWhatsAppCloudInboundResponseSchema = z.object({
  success: z.literal(true).describe('Webhook accepted. The agent turn runs after this ACK.'),
});

export type AgentWhatsAppCloudInboundAPI = API<
  z.infer<typeof agentWhatsAppCloudInboundAPISchema>,
  z.infer<typeof agentWhatsAppCloudInboundResponseSchema>
>;

/**
 * POST /agent/whatsapp/360dialog/partner — 360dialog Hub / Partner events.
 * Reserved for Embedded Signup. Today: log and 200.
 */
export const agentWhatsApp360dialogPartnerAPISchema = z
  .object({})
  .passthrough()
  .describe(
    '360dialog Partner / Hub webhook body. Shape varies by Hub event; parsed as opaque JSON.',
  );

export const agentWhatsApp360dialogPartnerResponseSchema = z.object({
  success: z.literal(true).describe('Partner event accepted.'),
});

export type AgentWhatsApp360dialogPartnerAPI = API<
  z.infer<typeof agentWhatsApp360dialogPartnerAPISchema>,
  z.infer<typeof agentWhatsApp360dialogPartnerResponseSchema>
>;

//* admin — bulk WhatsApp template send (SuperAdmin)

/**
 * `POST /agent/whatsapp/bulk-send` — send a catalog template to a list
 * of phones on one of Hero's WhatsApp lines. Each send is stamped on
 * the recipient's (account, phone) conversation so the agent remembers
 * the message when they reply later.
 *
 * @deprecated Superseded by `POST /message/campaigns` (audience
 * criteria, any prospect/client template, per-recipient language,
 * `MessageCampaign` audit row). Kept for existing scripts; no new
 * callers. See `message-campaign.api.ts`.
 *
 * Recipients who have opted out of outreach are skipped, and a
 * recipient the template could not actually reach (not registered with
 * the provider, and outside the 24h window) is reported in `failed[]`
 * rather than counted as sent.
 */
export const agentWhatsAppBulkSendAPISchema = z.object({
  phones: z
    .array(z.string().min(5).max(20))
    .min(1)
    .max(500)
    .describe('Recipient phones — E.164; `+`, spaces, and dashes tolerated.'),
  templateId: z
    .enum(['wathiqa_plus_promo', 'wathiqa_create_your_first_waybill'])
    .describe('Template id from the agent WhatsApp catalog.'),
  language: languageSchema.default('en').describe('Template locale for the whole batch.'),
  channelAccountId: z
    .string()
    .optional()
    .describe(
      'Which Hero-owned line sends the batch (e.g. the Wathiqa Plus number). Defaults to the ' +
        'primary platform line.',
    ),
  variables: z
    .record(z.string())
    .optional()
    .describe(
      'Template variables applied to EVERY recipient in the batch — a bulk list has no ' +
        'per-recipient data. Must cover all variables the template declares.',
    ),
});

export type AgentWhatsAppBulkSendAPI = API<
  z.infer<typeof agentWhatsAppBulkSendAPISchema>,
  { sent: number; failed: { phone: string; reason: string }[] }
>;

//* confirm a pending action
/**
 * Staged confirmation actions.
 *
 * This list is duplicated in three places that MUST stay in sync, or a
 * card stages fine and then fails to confirm:
 *
 *   1. here (`@hero/api`) — validates the confirm POST body
 *   2. `WidgetSpec.confirmation_card.action` in `@hero/schema` — validates
 *      the persisted assistant message
 *   3. `StagedConfirmation.action` in `agent.streamer.service.ts` — types
 *      the tool-result extraction
 */
export const agentConfirmationActionSchema = z.enum([
  'create_order',
  'create_client',
  'create_saved_location',
  'update_saved_location',
  'assign_driver_to_order',
  'assign_orders_to_vehicles',
  'create_financing_application',
  'accept_financing_offer',
  'update_order_status',
  'escalate_order',
  'create_contact',
  'update_contact',
  'delete_contact',
  'create_lead',
  'accept_offer',
  'decline_offer',
  'counter_offer',
  'issue_government_waybill',
  'edit_order',
  'bulk_edit_orders',
  'complete_signup',
  'connect_logisti',
  'create_driver',
  'create_vehicle',
  'create_driver_with_vehicle',
  'setup_owner_operator',
  'subscribe',
  'change_account',
]);

export type AgentConfirmationAction = z.infer<typeof agentConfirmationActionSchema>;

/**
 * Which kind of account chat is about to create — the first question of
 * the signup step.
 *
 * Deliberately narrower than `SignUpRequest`'s `personType`. That union
 * carries every shape the web wizard can produce
 * (`individual_customer`, a driver employed by a company they also
 * create); chat offers the two that a WhatsApp prospect actually is, and
 * each maps onto one `SignUpRequest` branch:
 *
 * | value | `SignUpRequest` | creates |
 * | --- | --- | --- |
 * | `company` | `personType: 'company'` | User + Org + membership |
 * | `freelancer_driver` | `personType: 'driver'`, `driverEmployment: 'freelancer'` | User + Driver, no Org |
 *
 * The distinction changes the rest of the funnel, not just this step: a
 * freelancer has no Org. They still give هوية صاحب العمل (a unified
 * number starting with 7) so Bayan can name the carrier as COMPANY + moi.
 */
export const agentSignupAccountTypeSchema = z.enum(['company', 'freelancer_driver']);
export type AgentSignupAccountType = z.infer<typeof agentSignupAccountTypeSchema>;

export const agentConfirmMessageAPISchema = z.object({
  conversationId: z.string().min(1),
  confirmationId: z.string().min(1),
  decision: z.enum(['confirm', 'decline']),
  /**
   * Staged action type from the confirmation card. Required as a fallback
   * when the user confirms before the assistant message is persisted
   * (SSE delivers the card before Mongo commit at end-of-turn).
   */
  action: agentConfirmationActionSchema.optional(),
  /** User edits from the confirmation card — merged over the staged payload. */
  payload: z.record(z.unknown()).optional(),
});
export type AgentConfirmMessageAPI = API<
  z.infer<typeof agentConfirmMessageAPISchema>,
  { ok: boolean; blocks?: unknown[] }
>;

//* conversation history (persistent ChatGPT-like thread list)

/**
 * `GET /agent/conversations` — the caller's conversation list, newest
 * activity first. Scoped server-side to threads the caller
 * participates in; there is no way to list someone else's threads.
 */
export const agentListConversationsAPISchema = z.object({
  limit: z.coerce.number().int().min(1).max(50).default(20),
  skip: z.coerce.number().int().min(0).default(0),
  /** Narrow to one channel surface (`in_app`, `whatsapp`, …). */
  channel: conversationChannelTypeSchema.optional(),
});

/** Slim list row — enough to render a thread sidebar without the transcript. */
export interface AgentConversationListItem {
  _id: string;
  channel: Conversation['channel'];
  status: Conversation['status'];
  language?: Conversation['language'];
  personaId?: Conversation['personaId'];
  lastMessageAt?: Date;
  unreadCount?: number;
  /** First ~120 chars of the newest message, for the sidebar preview. */
  preview?: string;
}
export type AgentListConversationsAPI = API<
  z.infer<typeof agentListConversationsAPISchema>,
  AgentConversationListItem[]
>;

/**
 * `GET /agent/conversations/messages` — full transcript of one
 * conversation, oldest-first. 403s when the caller is not a
 * participant. `before` (message id) pages backwards through history.
 */
export const agentGetTranscriptAPISchema = z.object({
  conversationId: z.string().min(1),
  limit: z.coerce.number().int().min(1).max(100).default(50),
  /** Return messages strictly older than this message id (backward paging). */
  before: z.string().optional(),
});
export type AgentGetTranscriptAPI = API<
  z.infer<typeof agentGetTranscriptAPISchema>,
  ConversationMessage[]
>;

//* admin — conversation reads (SuperAdmin CRM + Chat workspace)

/** `latest` = newest activity first; `oldest` = oldest activity first. */
export const agentAdminSortOrderSchema = z.enum(['latest', 'oldest']).default('latest');
export type AgentAdminSortOrder = z.infer<typeof agentAdminSortOrderSchema>;

export const agentAdminListConversationsAPISchema = z.object({
  /**
   * When set, list only that org's threads (`Conversation.org._id`).
   * When omitted, SuperAdmin sees every conversation (Chat workspace inbox).
   */
  orgId: objectIdSchema
    .optional()
    .describe('Optional org scope. Omit for the global Chat workspace inbox.'),
  leadId: objectIdSchema
    .optional()
    .describe(
      "Filter to the threads linked to one Lead (`Conversation.leadId`, written by `FunnelService.linkLead`). One-to-many — a prospect can be cold-reached on one line and reply on another — which is why it is the reverse of the lead's single-valued `meta.sourceMeta.conversationId`.",
    ),
  limit: z.coerce.number().int().min(1).max(50).default(20),
  skip: z.coerce.number().int().min(0).default(0),
  channel: conversationChannelTypeSchema.optional(),
  status: conversationStatusSchema.optional(),
  /**
   * Filter by last activity window on `Conversation.lastMessageAt`
   * (Today / Yesterday / This week / …). Bounds are computed in the
   * request timezone (`x-timezone`, falling back to Asia/Riyadh).
   */
  dateType: dateTypeSchema.optional(),
  search: searchColSchema
    .optional()
    .describe('Atlas Search over conversation subject / participant names when configured.'),
  sort: agentAdminSortOrderSchema
    .optional()
    .describe(
      "Sort by `lastMessageAt`: 'latest' (newest first) | 'oldest' (oldest first). Defaults to 'latest'.",
    ),
});

/** Admin list row — extends the caller-scoped item with CRM context. */
export interface AgentAdminConversationListItem extends AgentConversationListItem {
  participantName?: string;
  /**
   * The counterparty's phone, when the thread has one
   * (`Conversation.externalThreadId` for WhatsApp / SMS / voice).
   *
   * Separate from `participantName`, which prefers the WhatsApp profile
   * name and only falls back to the number — so on a thread with a
   * profile name the phone was unreachable. The lead drawer labels its
   * thread tabs with it, where "which number is this" is the question
   * being asked.
   */
  participantPhone?: string;
  subject?: string;
  /** Owning org id when the thread is tenant-scoped (absent for guest threads). */
  orgId?: string;
  /** Display name from `Conversation.org.name` when present. */
  orgName?: string;
  /** Who replies on this thread — `human` while staff hold it. */
  responderMode: Conversation['responderMode'];
  /** The staff member holding the thread, when `responderMode` is `human`. */
  assignedToUserId?: string;
  assignedToName?: string;
  takenOverAt?: Date;
  /** Staff changed the account during this takeover — the hand-back defaults to a memory reset. */
  takeoverTouchedIdentity?: boolean;
  /**
   * `Conversation.lastInboundAt` — the inbox composer derives the 24h
   * WhatsApp session window from it (free text inside, template outside).
   */
  lastInboundAt?: Date;
  /**
   * The platform records behind this thread, resolved by participant ids
   * and by the thread's phone. A Wathiqa Plus driver files with no
   * `Conversation.org`, so `orgId` alone called real accounts "guest".
   * Absent / empty → nobody has signed up with this number yet.
   */
  account?: AgentAdminConversationAccount;
}

export interface AgentAdminConversationAccount {
  userId?: string;
  userName?: string;
  driverId?: string;
  driverName?: string;
  /** `Conversation.org`, else the user's first org, else the driver's org. */
  orgId?: string;
  orgName?: string;
  /** `Conversation.leadId`, else the lead whose people carry this phone. */
  leadId?: string;
  leadTitle?: string;
}

export type AgentAdminListConversationsAPI = API<
  z.infer<typeof agentAdminListConversationsAPISchema>,
  AgentAdminConversationListItem[]
>;

/**
 * `GET /agent/admin/conversations/one` — one inbox row by id. The Chat
 * workspace deep link (`/chat?conversation=<id>`, from a ticket) lands
 * on a thread that may not be on the first page of the filtered list.
 */
export const agentAdminGetConversationAPISchema = z.object({
  conversationId: objectIdSchema,
});
export type AgentAdminGetConversationAPI = API<
  z.infer<typeof agentAdminGetConversationAPISchema>,
  AgentAdminConversationListItem
>;

//* admin — human takeover (SuperAdmin Chat workspace)

/**
 * `POST /agent/admin/conversations/messages` — a Hero staff member
 * replies on a thread, as staff.
 *
 * Sending implies **take over** (`responderMode: 'human'`) unless
 * `takeOver: false`; the AI stays out until someone hands back. On
 * WhatsApp the text goes out as session text and is refused with
 * `whatsapp_outside_session_window` when the 24h window is closed — use
 * a catalog template through `send()` in that case.
 */
export const agentAdminSendMessageAPISchema = z.object({
  conversationId: objectIdSchema.describe('The thread to reply on.'),
  text: z
    .string()
    .trim()
    .min(1)
    .max(4000)
    .describe('Plain text the recipient will read. Sent as-is; no markdown rendering on WhatsApp.'),
  takeOver: z
    .boolean()
    .default(true)
    .describe(
      'Flip the thread to `responderMode: human` so the AI does not answer the next inbound. Default true — a human speaking on the thread owns it.',
    ),
});
export type AgentAdminSendMessageAPI = API<
  z.infer<typeof agentAdminSendMessageAPISchema>,
  {
    conversation: AgentAdminConversationListItem;
    /** The staff bubble as persisted (pending on WhatsApp until the drain posts it). */
    message?: ConversationMessage;
  }
>;

/**
 * `POST /agent/admin/conversations/templates` — staff sends a catalog
 * WhatsApp template on an open thread (works outside the 24h session
 * window). Same template set as campaigns (prospect/client, WhatsApp).
 * See `docs/ai/human-takeover.md`.
 */
export const agentAdminSendTemplateAPISchema = z.object({
  conversationId: objectIdSchema.describe('The WhatsApp thread to send on.'),
  templateId: z
    .string()
    .min(1)
    .describe(
      'Catalog template id (e.g. `wathiqa_create_your_first_waybill`, `ticket_closed`). Must be campaign-eligible WhatsApp.',
    ),
  language: languageSchema
    .optional()
    .describe('Hub locale. Defaults to the conversation language, then `ar`.'),
  variables: z
    .record(z.string())
    .optional()
    .describe(
      'Named template variables (payload keys). Must cover every placeholder the catalog row declares; omit for variable-free templates.',
    ),
  takeOver: z
    .boolean()
    .default(false)
    .describe(
      'Rarely needed. Templates leave the AI in charge (like campaigns). If the thread is already held by staff, a successful send hands it back. Set true only to mute the AI after the send.',
    ),
});
export type AgentAdminSendTemplateAPI = API<
  z.infer<typeof agentAdminSendTemplateAPISchema>,
  {
    conversation: AgentAdminConversationListItem;
    /** Transcript bubble when the enqueue returned one; otherwise reload the transcript. */
    message?: ConversationMessage;
  }
>;

/**
 * `PATCH /agent/admin/conversations` — take over or hand back.
 *
 * `human` → the caller holds the thread: open confirmation cards are
 * voided, a system note is written, the AI is muted. `ai` → hand back:
 * a system note records what staff did (`note`) and the AI resumes on the
 * next inbound. With `reset`, `replayFrom` moves to that note so the
 * model never replays anything older; without it the conversation stays
 * as context. Escalation is separate and never muted.
 */
export const agentAdminPatchConversationAPISchema = z.object({
  conversationId: objectIdSchema,
  responderMode: z
    .enum(['ai', 'human'])
    .describe('`human` = take over (mute the AI); `ai` = hand back to the assistant.'),
  note: z
    .string()
    .trim()
    .max(2000)
    .optional()
    .describe(
      'Hand-back only. What staff did / fixed. Lands in the system note the model reads first after the hand-back — say what changed, not how to reply.',
    ),
  reset: z
    .boolean()
    .optional()
    .describe(
      'Hand-back only. `true` = reset the AI memory (older messages are no longer replayed; numbers in them are superseded). Omit to reset only when staff changed the account during this takeover (`takeoverTouchedIdentity`).',
    ),
});
export type AgentAdminPatchConversationAPI = API<
  z.infer<typeof agentAdminPatchConversationAPISchema>,
  {
    conversation: AgentAdminConversationListItem;
    note?: ConversationMessage;
    /** Hand-back only: whether the memory was reset. */
    reset?: boolean;
  }
>;

/**
 * `POST /agent/admin/conversations/notes` — a private note to the AI on
 * this thread («his truck is financed — never use the bank's 70»).
 *
 * Never sent to the customer and never takes over: `responderMode`,
 * `replayFrom` and open cards are unchanged, in AI and human mode alike.
 * Stored as a `system` row with `systemKind: 'staff_instruction'`; the
 * newest five are in the model's prompt on every later turn.
 */
export const agentAdminAddAiNoteAPISchema = z.object({
  conversationId: objectIdSchema.describe('The thread the note is about.'),
  text: z
    .string()
    .trim()
    .min(1)
    .max(1000)
    .describe(
      'What the AI should keep in mind. Written to the model, never shown to the customer.',
    ),
});
export type AgentAdminAddAiNoteAPI = API<
  z.infer<typeof agentAdminAddAiNoteAPISchema>,
  { conversation: AgentAdminConversationListItem; note?: ConversationMessage }
>;

/**
 * `POST /agent/admin/conversations/messages/voice-summary` — the voice
 * notes on one message summarized in `language` (cached per language on
 * `attachment.transcript.summaries`). Notes under ~15 words get none.
 */
export const agentAdminVoiceSummaryAPISchema = z.object({
  conversationId: objectIdSchema.describe('The thread the message is on.'),
  messageId: objectIdSchema.describe('The inbound message carrying the voice note(s).'),
  language: languageSchema.describe('Language the reader wants the summary in.'),
});
export type AgentAdminVoiceSummaryAPI = API<
  z.infer<typeof agentAdminVoiceSummaryAPISchema>,
  ConversationMessage
>;

/**
 * `POST /agent/admin/conversations/messages/translate` — one message
 * translated from its original into `language`, cached per language on
 * `translations`. Messages without words come back untranslated.
 */
export const agentAdminTranslateMessageAPISchema = z.object({
  conversationId: objectIdSchema.describe('The thread the message is on.'),
  messageId: objectIdSchema.describe('The message to translate.'),
  language: languageSchema.describe('Language the reader wants.'),
});
export type AgentAdminTranslateMessageAPI = API<
  z.infer<typeof agentAdminTranslateMessageAPISchema>,
  ConversationMessage
>;

//* admin — thread ops (Wathiqa Plus recovery from the inbox)

/**
 * The CRM client a driver / truck belongs to when it is not the account's
 * own (`Driver.client` / `Vehicle.client`, ADR-007) — another carrier a
 * broker subcontracts, a customer's own truck. The وثيقة is filed under
 * this client (`crNumber` as Bayan `carrier.moi`), not the account.
 */
export type AgentAdminFleetClient = {
  _id: string;
  name: string;
  roles: PartyRole[];
  /** `Client.legal.registrationId` — the 70… the وثيقة declares. */
  crNumber?: string;
};

/**
 * What the database says about the account behind a thread — the same
 * rows `LogistiWaybillService.issue()` re-reads at filing time and the
 * agent's `onFile` snapshot renders from. The transcript is not a
 * source here; that is the point.
 */
export interface AgentAdminThreadIdentity {
  conversationId: string;
  business: 'hero' | 'wathiqa_plus';
  /** The WhatsApp number the thread runs on — where the وثيقة is delivered. */
  phone?: string;
  org?: {
    _id: string;
    name?: string;
    /** `Org.legal.registrationId` — the unified number a company files under. */
    crNumber?: string;
    /** What `member/admin-create-member` takes as `orgType`. */
    orgType: 'business_customer' | 'carrier_company' | 'broker';
  };
  driver?: {
    _id: string;
    name?: string;
    phone?: string;
    /** `Driver.legal.id` — the current هوية / إقامة number (10 digits). */
    id?: string;
    /** `Driver.legal.idIssueNumber` — رقم النسخة. */
    idIssueNumber?: number;
    /**
     * `Driver.legal.employerIdentity` — هوية صاحب العمل / الرقم الموحد the
     * driver works under (typed, read off a card, or staff-entered).
     * A current value, not proof Bayan accepted it.
     */
    employerIdentity?: string;
    /** `legal.extraction.employerIdentity` — the raw card read, for comparison. */
    employerRead?: string;
    /** `Driver.legal.carrierIdentity` — the 70… Bayan accepted on a filed وثيقة. */
    carrierIdentity?: string;
    /** Alternate 70… numbers OCR found on the cards. */
    moiCandidates?: string[];
    /** The id / issue number on file match `proof` (Bayan or staff). */
    idProven: boolean;
    /** Bayan accepted this exact id + issue number on a filed وثيقة. */
    bayanProven: boolean;
    /** Last accepted (Bayan) or staff-verified pair, as stored on the row. */
    proof?: AgentAdminIdentityProof;
    /** @deprecated Read `carrierIdentity` / `employerIdentity`. */
    carrierMoi?: string;
    /** @deprecated Read `carrierIdentity`. */
    carrierMoiProven: boolean;
    /** Unset: the account's own driver. */
    client?: AgentAdminFleetClient;
  };
  vehicle?: {
    _id: string;
    plate?: string;
    /** نوع اللوحة. Unset means Bayan uses the public-transport default. */
    plateType?:
      | 'public_transport'
      | 'private_transport'
      | 'heavy_equipment'
      | 'export'
      | 'temporary';
    /** هوية المالك — a person id, a company 70…, or a bank / financier. */
    ownerIdentity?: string;
    /** Who the owner is: `financier` owners are tried last, never dropped. */
    ownerKind?: VehicleOwnerKind;
    /** اسم المالك as printed on the استمارة. */
    ownerLegalName?: string;
    /**
     * هوية المستخدم — who operates the truck (a 70… establishment or a
     * person id). Usually the carrier when a bank owns the truck.
     */
    operatorIdentity?: string;
    /** `registration.carrierIdentity` — the 70… Bayan accepted with this plate. */
    carrierIdentity?: string;
    /** Raw استمارة reads (`registration.extraction`), for read-vs-current. */
    read?: {
      ownerIdentity?: string;
      ownerName?: string;
      ownerKind?: VehicleOwnerKind;
      operatorIdentity?: string;
    };
    /** Bayan accepted this plate on a filed وثيقة, or staff verified it. */
    plateProven: boolean;
    /** Last accepted (Bayan) or staff-verified pair, as stored on the row. */
    proof?: AgentAdminIdentityProof;
    /** @deprecated Read `carrierIdentity` / `operatorIdentity` / `ownerIdentity`. */
    carrierMoi?: string;
    /** @deprecated Read `carrierIdentity`. */
    carrierMoiProven: boolean;
    /** Unset: the account's own truck. */
    client?: AgentAdminFleetClient;
  };
  /**
   * Whose fleet this is. `org`: a company — drivers and trucks are the
   * company's and staff can add more. `owner_operator`: one driver filing
   * for himself; a second driver needs the account moved to a company first.
   */
  account: { kind: 'org' | 'owner_operator' | 'none'; canAddDriver: boolean };
  /**
   * The person on the thread, when they have an account — what account ops
   * (`to_org_account`, `to_driver_account`, `set_member_driver`) act on.
   */
  user?: { _id: string; name?: string };
  /** WhatsApp profile name — the default name when staff finish a signup. */
  profileName?: string;
  /**
   * Every driver / truck on the account, read from the rows — the same ones
   * the assistant offers on the next turn. `driver` / `vehicle` above are the
   * default pick (last used on a وثيقة, else the newest).
   */
  drivers: NonNullable<AgentAdminThreadIdentity['driver']>[];
  vehicles: (NonNullable<AgentAdminThreadIdentity['vehicle']> & {
    driverId?: string;
    driverName?: string;
  })[];
  /** Filed وثائق on this account, newest first. */
  waybills: AgentAdminThreadWaybill[];
  /** Newest order card on the thread, if any. */
  order?: {
    _id: string;
    ref?: string;
    waybill?: OrderWaybillBayanStatus;
    /**
     * Cargo Bayan files from. Staff edit these when the AI set `packing=other`
     * on a حاوية / bulk load and Bayan returned `[dimensions is required]`.
     */
    cargo?: {
      category: 'goods' | 'container';
      itemName?: string;
      packing?: 'pallet' | 'package' | 'unpackable_item' | 'bag' | 'barrel' | 'other';
      quantity?: number;
      /** Total item weight in tons (UI unit). */
      weightTons?: number;
    };
  };
  /**
   * What `LogistiWaybillService.issue()` will send first. Same helpers as
   * the assistant — not a second mapper. `carrierIdentity` is written only
   * after Bayan accepts, inside `issue()`.
   */
  filing: {
    driverId?: string;
    driverIdReady: boolean;
    issueNumber: string;
    issueNumberOnFile: boolean;
    carrierMoi?: string;
    carrierSource: string;
    carrierReady: boolean;
    carrierProven: boolean;
    alsoTry: string[];
    /**
     * Every 70… Bayan will try, in order. `slot` is the strongest reason a
     * number is on the list; `slots` all of them (`typed`, `proven_vehicle`,
     * `vehicle_operator`, `financier_owner`, …).
     */
    tryOrder: Array<{ moi: string; slot: string; slots: string[]; name?: string }>;
    plate?: string;
    plateReady: boolean;
    plateType: string;
    plateTypeOnFile: boolean;
  };
}

/**
 * The `identityProof` snapshot stored identically on the driver and the
 * vehicle: which pair was accepted together, by whom, and when. Compare
 * `driverRef` / `vehicleRef` to spot a driver swap.
 */
export interface AgentAdminIdentityProof {
  by: 'bayan' | 'staff';
  at: Date;
  staffUserId?: string;
  tripId?: string;
  driverRef?: string;
  identityNumber?: string;
  issueNumber?: number;
  vehicleRef?: string;
  plate?: string;
  carrierMoi?: string;
  waybillNumber?: string;
  driverName?: string;
  carrierName?: string;
  /** The vehicle's proof names a different driver than this one. */
  otherDriver?: boolean;
}

/** One filed وثيقة on the account, as the admin card shows it. */
export interface AgentAdminThreadWaybill {
  orderId: string;
  ref?: string;
  tripId: string;
  status?: OrderWaybillBayanStatus['status'];
  issuedAt?: Date;
  pdfUrl?: string;
  fromCity?: string;
  toCity?: string;
  driverId?: string;
  driverName?: string;
  vehicleId?: string;
  plate?: string;
  senderName?: string;
  recipientName?: string;
  pickupDate?: Date;
  deliveryDate?: Date;
  cargo?: NonNullable<AgentAdminThreadIdentity['order']>['cargo'];
  /** Bayan's own refusal on the last attempt, verbatim. */
  lastError?: string;
}

export const agentAdminThreadIdentityAPISchema = z.object({
  conversationId: objectIdSchema,
});
export type AgentAdminThreadIdentityAPI = API<
  z.infer<typeof agentAdminThreadIdentityAPISchema>,
  AgentAdminThreadIdentity
>;

/**
 * `GET /agent/admin/conversations/filing` — {@link AgentAdminThreadIdentity.filing}
 * for the driver / truck staff picked on the New وثيقة form, resolved the
 * way `createLane` resolves them. Omit both for the thread's default pair.
 */
export const agentAdminThreadFilingAPISchema = z.object({
  conversationId: objectIdSchema,
  driverId: objectIdSchema.optional(),
  vehicleId: objectIdSchema.optional(),
});
export type AgentAdminThreadFilingAPI = API<
  z.infer<typeof agentAdminThreadFilingAPISchema>,
  AgentAdminThreadIdentity['filing']
>;

/**
 * `PATCH /agent/admin/conversations/identity` — staff correct the
 * identity fields on the live documents (never in chat text).
 *
 * Writes land in the CURRENT top-level values (`Driver.legal.id` /
 * `idIssueNumber` / `employerIdentity`, `Vehicle.registration.plate` /
 * `plateType` / `ownerIdentity` / `ownerKind` / `operatorIdentity`,
 * `Org.legal.registrationId`) and stamp `identityProof {by: 'staff'}` on
 * the rows touched — unless the row's Bayan proof still matches, which is
 * kept. Staff never write `carrierIdentity`: that is only what Bayan
 * accepted. The next AI turn renders these as `verified` in `onFile`.
 */
/**
 * Cargo Bayan files from — `load.category` and the first item (name /
 * packing / quantity / weight), the slots the Bayan mapper reads.
 * Clearing packing (`null`) lets bulk freight remap to Ton; category
 * `container` files as unitId=Container.
 */
export const agentAdminThreadCargoSchema = z.object({
  category: tripLoadTypeSchema.optional(),
  itemName: z.string().trim().min(1).max(200).optional(),
  packing: itemPackingSchema.nullable().optional(),
  quantity: z.coerce.number().int().positive().max(10_000).optional(),
  weightTons: z.coerce.number().positive().max(1000).optional(),
});
export type AgentAdminThreadCargo = z.infer<typeof agentAdminThreadCargoSchema>;

export const agentAdminUpdateThreadIdentityAPISchema = z.object({
  conversationId: objectIdSchema,
  driver: z
    .object({
      driverId: objectIdSchema
        .optional()
        .describe("Which driver on the account. Defaults to the thread's default pick."),
      id: z
        .string()
        .trim()
        .optional()
        .describe('National id / iqama — exactly 10 digits after normalisation.'),
      idIssueNumber: z.coerce.number().int().positive().optional().describe('رقم النسخة.'),
      employerIdentity: z
        .string()
        .trim()
        .optional()
        .describe(
          'هوية صاحب العمل — the employer 70… on the iqama. Stored as `Driver.legal.employerIdentity`.',
        ),
      /** @deprecated Sent as `employerIdentity`. */
      carrierMoi: z.string().trim().optional().describe('Deprecated alias of `employerIdentity`.'),
    })
    .optional(),
  vehicle: z
    .object({
      vehicleId: objectIdSchema
        .optional()
        .describe("Which truck on the account. Defaults to the thread's default pick."),
      plate: z
        .string()
        .trim()
        .optional()
        .describe(
          'Saudi plate as printed (Arabic or Latin). Stored as the canonical Latin form, e.g. RRA6587.',
        ),
      plateType: vehiclePlateTypeSchema
        .optional()
        .describe('نوع اللوحة. Omit to leave the current category.'),
      ownerIdentity: z
        .string()
        .trim()
        .optional()
        .describe('هوية المالك on the استمارة — a 70… company or a 10-digit person id.'),
      ownerKind: vehicleOwnerKindSchema
        .optional()
        .describe(
          'company / individual / financier (a bank or financing house — tried last by Bayan).',
        ),
      operatorIdentity: z
        .string()
        .trim()
        .optional()
        .describe('هوية المستخدم on the استمارة — a 70… company or a 10-digit person id.'),
      /** @deprecated Sent as `operatorIdentity`. */
      carrierMoi: z.string().trim().optional().describe('Deprecated alias of `operatorIdentity`.'),
    })
    .optional(),
  org: z
    .object({
      crNumber: z
        .string()
        .trim()
        .optional()
        .describe('Company unified number — 10 digits starting with 7.'),
    })
    .optional(),
  /**
   * Cargo on the thread's newest order (or `orderId` when set). Same slots
   * the Bayan mapper reads — `load.category`, first item name / packing /
   * quantity / weight. Clearing packing (`null`) lets bulk freight remap
   * to Ton; setting category `container` files as unitId=Container.
   */
  order: agentAdminThreadCargoSchema
    .extend({
      orderId: objectIdSchema.optional().describe("Defaults to the thread's newest order card."),
    })
    .optional(),
});
export type AgentAdminUpdateThreadIdentityAPI = API<
  z.infer<typeof agentAdminUpdateThreadIdentityAPISchema>,
  AgentAdminThreadIdentity
>;

/**
 * `POST /agent/admin/conversations/issue-waybill` — file the government
 * وثيقة for the thread's order and deliver the PDF to the thread's
 * number. SuperAdmin acts for the owner; `issue()` is idempotent.
 */
export const agentAdminIssueThreadWaybillAPISchema = z.object({
  conversationId: objectIdSchema,
  orderId: objectIdSchema
    .optional()
    .describe(
      "Defaults to the thread's newest order card. Pass to pick another order on the same account.",
    ),
  /**
   * File the وثيقة for this lane on the thread's newest unfiled order for
   * the same two cities (patched with the cargo, dates, parties, driver and
   * truck sent here), or on a new order when there is none.
   * Stops are `Region._id`s (`type: 'city'` or `'hub'`). A region without
   * `metadata.bayanCityId` is refused by Bayan (`city_unmapped`).
   */
  lane: z
    .object({
      fromRegionId: objectIdSchema.describe('Pickup city.'),
      toRegionId: objectIdSchema.describe('Dropoff city.'),
      pickupDate: z.coerce.date().optional().describe('Loading day. Omit, or a past day, for now.'),
      deliveryDate: z.coerce
        .date()
        .optional()
        .describe(
          "Delivery day. Omit for Bayan's default (loading + 1 day). Bayan files at most 5 days after loading.",
        ),
      /** Cargo on the new order. Omitted slots keep the default (بضائع عامة, 27 t). */
      cargo: agentAdminThreadCargoSchema.optional(),
    })
    .optional(),
  /**
   * Parties the وثيقة names (المرسل / المستلم). Stored as a name-only
   * contact on the pickup / last drop-off — on a new `lane` order, or on
   * the existing order before it is filed. Omit to keep the chat contact;
   * Bayan's phone falls back to the account's number either way.
   */
  senderName: z.string().trim().max(200).optional(),
  recipientName: z.string().trim().max(200).optional(),
  /**
   * The 70… to try first on this filing (the `typed` slot). Omit to use the
   * same order the assistant would (`rankCarrierMoiCandidates`). A different number is for a
   * carrier issuing on behalf of another; it is not saved unless Bayan accepts.
   */
  carrierMoi: z.string().trim().optional(),
  /**
   * Driver / truck on a new `lane` وثيقة. Default: the thread's pick (last
   * used, else newest). Any row on the account — a company with several
   * drivers files each trip against the one driving it.
   */
  driverId: objectIdSchema.optional(),
  vehicleId: objectIdSchema.optional(),
  /**
   * Bayan good type for every item on this filing — the same override the
   * dashboard's waybill drawer sends. Omit to resolve from the item type.
   */
  goodTypeId: z.coerce.number().int().positive().optional(),
});

/**
 * `PATCH /agent/admin/conversations/order` — staff change anything on a
 * thread's order: cities, cargo, parties, dates, driver, truck.
 *
 * A live وثيقة is cancelled first, the edits are applied, then one
 * replacement is filed (`LogistiWaybillService.reissue`) and sent where the
 * old one went. Cancelling first keeps the order hooks from re-filing on
 * every intermediate write. An order with no وثيقة is only saved.
 */
export const agentAdminEditThreadOrderAPISchema = z.object({
  conversationId: objectIdSchema,
  orderId: objectIdSchema,
  /** Omitted stops keep their city. */
  fromRegionId: objectIdSchema.optional(),
  toRegionId: objectIdSchema.optional(),
  cargo: agentAdminThreadCargoSchema.optional(),
  /** Bayan good type for the replacement. Omit to resolve from the item type. */
  goodTypeId: z.coerce.number().int().positive().optional(),
  senderName: z.string().trim().max(200).optional(),
  recipientName: z.string().trim().max(200).optional(),
  pickupDate: z.coerce.date().optional(),
  deliveryDate: z.coerce.date().optional(),
  driverId: objectIdSchema.optional(),
  vehicleId: objectIdSchema.optional(),
});
export type AgentAdminEditThreadOrderAPI = API<
  z.infer<typeof agentAdminEditThreadOrderAPISchema>,
  {
    identity: AgentAdminThreadIdentity;
    /** The order's وثيقة after the edit. */
    status?: OrderWaybillBayanStatus;
    /** Set when a live وثيقة was cancelled and a replacement attempted. */
    refiled?: { voidedTripId: string; tripId?: string };
    failure?: AgentAdminThreadOpsFailure;
  }
>;

/**
 * `POST /agent/admin/conversations/read-documents` — staff drop the
 * driver's cards (هوية / إقامة, رخصة, استمارة) and get the fields back to
 * fill the driver / truck forms. Same identity reader and normalisers as
 * a photo sent in chat; nothing is saved — the forms save through the
 * Drivers / Vehicles endpoints.
 */
export const agentAdminReadThreadDocumentsAPISchema = z.object({
  conversationId: objectIdSchema,
  images: z.array(imageWithUploadSchema).min(1).max(6),
});

/**
 * Who a thread account's driver / truck belongs to: a CRM client of the
 * account (`clientId`) or its own fleet (`null`). A driver's truck moves
 * with him. Same write as the agent's «تابع لشركة X».
 */
export const agentAdminSetThreadFleetClientAPISchema = z
  .object({
    conversationId: objectIdSchema,
    driverId: objectIdSchema.optional(),
    vehicleId: objectIdSchema.optional(),
    clientId: objectIdSchema.nullable(),
  })
  .refine(body => Boolean(body.driverId || body.vehicleId), {
    message: 'driverId or vehicleId is required',
  });
export type AgentAdminSetThreadFleetClientAPI = API<
  z.infer<typeof agentAdminSetThreadFleetClientAPISchema>,
  { client?: AgentAdminFleetClient }
>;

export type AgentAdminDocumentsRead = {
  /** The photos as stored — `{id, url}`, reuse them on the driver / truck. */
  images: Image[];
  ok: boolean;
  /** Why nothing was read (`unreadable`, `wrong_document_type`, `provider_error`). */
  reason?: string;
  documents: ('iqama' | 'license' | 'istimara')[];
  /** Number fields whose two reads disagreed — check them by eye. */
  uncertain: string[];
  driver: {
    fullName?: string;
    /** Saudi mobile (`9665…`) written on or next to a photo — cards never print one. */
    phone?: string;
    id?: string;
    idIssueNumber?: number;
    nationality?: NationalityPerson;
    employerIdentity?: string;
    idImageUrl?: string;
    licenseImageUrl?: string;
  };
  vehicle: {
    plate?: string;
    plateType?: z.infer<typeof vehiclePlateTypeSchema>;
    type?: VehicleTypes;
    brand?: string;
    color?: string;
    year?: number;
    chassisNumber?: string;
    serialNumber?: string;
    expiryDate?: string;
    ownerName?: string;
    ownerIdentity?: string;
    ownerKind?: VehicleOwnerKind;
    operatorIdentity?: string;
    registrationImageUrl?: string;
  };
};

export type AgentAdminReadThreadDocumentsAPI = API<
  z.infer<typeof agentAdminReadThreadDocumentsAPISchema>,
  AgentAdminDocumentsRead
>;

/**
 * Why a press did not file. `errorId` is the backend catalog id;
 * `message` its English sentence; Bayan's own refusal is on
 * `status.lastError`.
 */
export type AgentAdminThreadOpsFailure = {
  errorId: string;
  message: string;
};

/**
 * What happened to the PDF on this press. `issued`: filed now; the PDF is
 * sent once Bayan renders it. `resent`: already filed; `sent` says whether
 * WhatsApp accepted the copy, `reason` why not.
 */
export type AgentAdminWaybillDelivery = {
  kind: 'issued' | 'resent';
  sent: boolean;
  reason?: string;
};

export type AgentAdminIssueThreadWaybillAPI = API<
  z.infer<typeof agentAdminIssueThreadWaybillAPISchema>,
  {
    status: OrderWaybillBayanStatus;
    identity: AgentAdminThreadIdentity;
    delivery?: AgentAdminWaybillDelivery;
    /** Set when the press did not file and the reason is not a blocker. */
    failure?: AgentAdminThreadOpsFailure;
  }
>;

export const agentAdminGetTranscriptAPISchema = z.object({
  /**
   * Optional org guard. When set, the conversation must belong to this
   * org (CRM tab). When omitted, SuperAdmin may open any transcript
   * (Chat workspace — including guest / org-less threads).
   */
  orgId: objectIdSchema.optional(),
  conversationId: z.string().min(1),
  limit: z.coerce.number().int().min(1).max(100).default(50),
  before: z.string().optional(),
  sort: agentAdminSortOrderSchema
    .optional()
    .describe(
      "Sort messages by `_id`: 'latest' (newest first) | 'oldest' (oldest first). Defaults to 'oldest'.",
    ),
});

export type AgentAdminGetTranscriptAPI = API<
  z.infer<typeof agentAdminGetTranscriptAPISchema>,
  ConversationMessage[]
>;

/**
 * `GET /agent/media/url` — resolve a stored attachment into a URL that
 * can actually be played.
 *
 * Voice audio lives in a private GCS bucket (ADR-006), so
 * `attachment.url` is an object address, not a readable link. This
 * endpoint authorizes the caller against the conversation that owns
 * the clip and returns a short-lived signed URL.
 *
 * It returns the URL as JSON rather than 302-redirecting because a
 * browser cannot attach a bearer token to `<audio src>` — the player
 * fetches the URL first, then sets `src` to what comes back.
 */
export const agentMediaUrlAPISchema = z.object({
  conversationId: z
    .string()
    .min(1)
    .describe('Conversation that owns the attachment. Authorization is checked against it.'),
  objectPath: z
    .string()
    .min(1)
    .describe(
      'Storage object path from `attachment.storage.objectPath`. Must belong to an attachment on the given conversation.',
    ),
});

export type AgentMediaUrlAPI = API<
  z.infer<typeof agentMediaUrlAPISchema>,
  {
    /** Signed, short-lived, single-object read URL. */
    url: string;
    /** When the signature stops working, so clients can refetch. */
    expiresAt: Date;
  }
>;

/**
 * `GET /agent/runs/stream` — re-attach a dropped SSE stream via the
 * `resumeToken` from `run_started`. Token deltas are not replayed
 * (they are not buffered server-side); the resumed stream waits for
 * the run to reach a terminal state, then emits the committed
 * assistant message(s) (`assistant_message_committed`) followed by
 * `run_finished`. Enough for a client that lost its connection
 * mid-turn to recover the final answer.
 */
export const agentResumeRunAPISchema = z.object({
  resumeToken: z.string().min(1),
});
/** Response is an SSE stream — same marker convention as `AgentSendMessageAPI`. */
export type AgentResumeRunAPI = API<
  z.infer<typeof agentResumeRunAPISchema>,
  { runId: string; conversationId: string }
>;

//* SSE event union (mirrored from backend `agent.sse.events.ts`)

const widgetSpecPayloadSchema = z.object({}).passthrough();

const agentSSEEventSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('run_started'),
    runId: z.string(),
    conversationId: z.string(),
    resumeToken: z.string(),
  }),
  z.object({
    type: z.literal('status_update'),
    runId: z.string(),
    phase: z.enum(['thinking', 'routing', 'planning', 'calling_tool', 'composing']),
    labelEn: z.string(),
    labelAr: z.string(),
    labelUr: z.string(),
    toolName: z.string().optional(),
  }),
  z.object({
    type: z.literal('assistant_token'),
    runId: z.string(),
    delta: z.string(),
  }),
  z.object({
    type: z.literal('tool_started'),
    runId: z.string(),
    toolCallId: z.string(),
    name: z.string(),
    labelEn: z.string().optional(),
    labelAr: z.string().optional(),
  }),
  z.object({
    type: z.literal('tool_finished'),
    runId: z.string(),
    toolCallId: z.string(),
    name: z.string(),
    ok: z.boolean(),
    errorMessage: z.string().optional(),
  }),
  z.object({
    type: z.literal('assistant_widget'),
    runId: z.string(),
    widget: widgetSpecPayloadSchema,
  }),
  z.object({
    type: z.literal('confirmation_required'),
    runId: z.string(),
    confirmationId: z.string(),
    action: z.string(),
    payload: z.record(z.unknown()),
  }),
  z.object({
    type: z.literal('assistant_message_committed'),
    runId: z.string(),
    conversationMessageId: z.string(),
    blocks: z.array(z.unknown()),
    /** Mid-turn flush — see backend `AgentSSEEvent`. */
    immediate: z.boolean().optional(),
    /** The immediate bubble is the turn's whole reply — see backend `AgentSSEEvent`. */
    final: z.boolean().optional(),
  }),
  z.object({
    type: z.literal('run_finished'),
    runId: z.string(),
    status: z.enum(['completed', 'failed', 'awaiting_human', 'cancelled']),
  }),
  z.object({
    type: z.literal('error'),
    runId: z.string(),
    /**
     * Stable, user-safe error code. The frontend localizes via
     * `m-agent.json` `errors.<code>`. Optional for back-compat —
     * older streamed events that only carry `message` will fall
     * back to `'unknown'` on the client.
     */
    code: z
      .enum(['unauthorized', 'rate_limited', 'configuration', 'timeout', 'cancelled', 'unknown'])
      .optional(),
    /**
     * English fallback for clients that haven't loaded `m-agent`
     * translations. Intentionally generic — never contains schema
     * details or stack traces.
     */
    message: z.string(),
    /** Sentry / log reference id — surfaced for support. */
    referenceId: z.string().optional(),
  }),
]);

export type AgentSSEEvent = z.infer<typeof agentSSEEventSchema>;
export const AgentSSEEventSchema = agentSSEEventSchema;
